import React, { useEffect, useMemo, useState } from "react";
import { Info } from "lucide-react";
import { useTranslation } from "react-i18next";
import releaseAnnouncement from "../content/releaseAnnouncement";

const STORAGE_PREFIX = "pubmed-release-announcement";

const resolveUserId = (user) =>
  user?.id || user?.user_id || user?.UserId || "anonymous";

const resolveAnnouncementContent = (language) => {
  const normalizedLanguage = String(language || "en").split("-")[0].toLowerCase();
  return (
    releaseAnnouncement.translations[normalizedLanguage] ||
    releaseAnnouncement.translations.en ||
    releaseAnnouncement.translations.ja
  );
};

function ReleaseAnnouncementModal({ user }) {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const userId = resolveUserId(user);
  const storageKey = `${STORAGE_PREFIX}:${releaseAnnouncement.id}:${userId}`;
  const content = useMemo(
    () => resolveAnnouncementContent(i18n.language),
    [i18n.language],
  );

  useEffect(() => {
    if (typeof window === "undefined" || !userId || !releaseAnnouncement?.id) {
      return;
    }
    const acknowledged = window.localStorage.getItem(storageKey);
    setIsOpen(acknowledged !== "true");
  }, [storageKey, userId]);

  const handleConfirm = () => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(storageKey, "true");
    }
    setIsOpen(false);
  };

  if (!isOpen || !content) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[100] overflow-y-auto">
      <div className="fixed inset-0 bg-slate-900/55" />
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="relative w-full max-w-2xl overflow-hidden rounded-lg bg-white shadow-2xl">
          <div className="border-b border-slate-200 px-6 py-5">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 rounded-lg bg-blue-100 p-2 text-blue-700">
                <Info className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-500">
                  {content.dateLabel}：{releaseAnnouncement.publishedAt}
                </p>
                <p className="mt-3 text-sm font-semibold uppercase tracking-wide text-slate-500">
                  {content.titleLabel}
                </p>
                <h2 className="mt-1 text-2xl font-bold text-slate-900">
                  {content.title}
                </h2>
              </div>
            </div>
          </div>

          <div className="px-6 py-6">
            <p className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-500">
              {content.bodyLabel}
            </p>
            <div className="space-y-4 text-base leading-8 text-slate-700">
              {content.body.map((paragraph) => (
                <p key={paragraph} className="whitespace-pre-line">
                  {paragraph}
                </p>
              ))}
            </div>
          </div>

          <div className="border-t border-slate-200 px-6 py-4">
            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleConfirm}
                className="rounded-md bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-slate-800"
              >
                {content.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ReleaseAnnouncementModal;
